import { getUserAgent } from '@calm/evexia-web-common/dist/user-agent';
import isSSR from '@calm/web-common/dist/isSSR';
import { extractAppConfig } from '@rhg/hooks/use-app-config';
import { RHG_HEADER } from '@rhg/utils/constants';
import UAParser from 'ua-parser-js';

export function getUserOs(): UAParser.IOS {
    if (isSSR()) {
        return { name: '', version: '' };
    }
    const userAgent = getUserAgent();
    // To see a list of possible OS names, Introspect the name property in your IDE
    return userAgent.os;
}

export function getUserDevice(): UAParser.IDevice {
    if (isSSR()) {
        return { model: '', type: '', vendor: '' };
    }
    const userAgent = getUserAgent();
    return userAgent.device;
}

/** To see a list of possible OS names, Introspect the name property in your IDE */
export function getUserBrowser(): UAParser.IBrowser {
    if (isSSR()) {
        return { name: '', version: '', major: '' };
    }
    const userAgent = getUserAgent();
    return userAgent.browser;
}

export function isUserOsMobile() {
    const os = getUserOs().name;
    return os === 'Android' || os === 'iOS';
}

export function isSafari() {
    if (isSSR()) {
        return false;
    }
    return !!getUserBrowser().name?.includes('Safari');
}

export function isIOS() {
    return getUserOs().name === 'iOS';
}

export function getRhgAppHeaderObject() {
    if (isSSR()) {
        return {};
    }

    const { NEXT_PUBLIC_VERSION } = extractAppConfig();
    const os = getUserOs();
    const browser = getUserBrowser().name;
    const isMobileBrowser = isUserOsMobile();

    return {
        /**
         * Only used in logging
         */
        app: 'evexia_web',
        /**
         * Used by the media service to serve correct content
         * DeviceType in reporting
         */
        platform: isMobileBrowser ? os.name?.toLowerCase() : 'www',
        /**
         * AppVersion in reporting
         */
        version: NEXT_PUBLIC_VERSION,
        /**
         * DeviceOS in reporting
         */
        os: `${os.name} ${os.version}`,
        /**
         * DeviceModel in reporting
         */
        'device-model': browser,
    };
}

export function formattedKeyValuePairs(
    headerValues: { [key: string]: string | undefined } = {}
) {
    return Object.entries(headerValues).reduce((acc, [key, value]) => {
        if (value) {
            // Be sure to remove '=' and ';' characters from the values
            return `${acc}${key}=${value.replace(/[=;]/g, '')};`;
        }
        return acc;
    }, '');
}

export function getRhgAppHeader() {
    if (isSSR()) {
        return '';
    }
    return formattedKeyValuePairs(getRhgAppHeaderObject());
}

export function getRhgE2ETestHeader() {
    if (isSSR()) {
        return '';
    }

    if (window.navigator.userAgent === 'calm-cypress-user-agent') {
        return 'undefined-evexia-e2e;';
    }
}

export function getFormattedRhgHeader(
    header: RHG_HEADER,
    keyValuePairs: { [key: string]: string | undefined } = {}
) {
    return {
        [header]: formattedKeyValuePairs(keyValuePairs),
    };
}

export function getRhgAppAppendHeader(
    keyValuePairs: { [key: string]: string | undefined } = {}
) {
    return getFormattedRhgHeader(RHG_HEADER.APP_APPEND, keyValuePairs);
}

// This helper function exists so it can be easily mocked in tests
export function getNavigator() {
    if (typeof window !== 'undefined') {
        return navigator;
    }
    return {} as Navigator;
}
